import { Typography } from '@tunasong/ui-lib'
import { capitalize } from '@tunasong/models'
import { Divider, VBox, useRedraw, useRedrawInterval } from '@tunasong/ui-lib'
import type { FC, HTMLAttributes } from 'react'
import React, { Fragment } from 'react'
import AudioPermissions from './audio-permissions.js'
import DeviceSelect from './device-select.js'
import ChordView from './dsp/chord-view.js'
import ChromaView from './dsp/chroma-view.js'
import { TunerView } from './dsp/index.js'
import { useAudioEngine } from './hooks/index.js'
import { useMic } from './hooks/mic.js'
import { Volume } from './volume.js'

export interface AudioMixerProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
}

export const AudioMixer: FC<AudioMixerProps> = props => {
  const { className, ...restProps } = props

  const engine = useAudioEngine()

  /** Since we change values in the mixer we need to explicitly redraw when changing */
  const refresh = useRedraw()

  /** Refresh every second to handle updated mixer settings */
  useRedrawInterval(500)

  const { selectedDevice, setDevice } = useMic()

  if (!engine) {
    return null
  }
  const { mixer } = engine

  const { sampleRate } = mixer.context

  const { channels, busses } = mixer

  return (
    <VBox className={className} {...restProps}>
      <AudioPermissions />
      <DeviceSelect type="audioinput" selectedId={selectedDevice?.deviceId} onChange={setDevice} />

      <Divider style={{ marginTop: 8 }}>Sources</Divider>

      {channels.map(channel => (
        <Fragment key={channel.id}>
          <Volume controller={channel} title={capitalize(channel.name)} onChange={refresh} />
          {channel.monitor ? (
            <>
              <TunerView audioController={channel} />
              <ChromaView audioController={channel} />
              <ChordView style={{ fontSize: 32 }} audioController={channel} />
            </>
          ) : null}
        </Fragment>
      ))}

      <Divider style={{ marginTop: 16 }}>Busses</Divider>
      {busses.map(bus => (
        <React.Fragment key={bus.name}>
          <Volume key={bus.name} controller={bus} title={capitalize(bus.name)} onChange={refresh} />
          {bus.monitor ? (
            <>
              <ChromaView audioController={bus} />
              <ChordView style={{ fontSize: 32 }} audioController={bus} />
            </>
          ) : null}
        </React.Fragment>
      ))}
      <Divider />
      <Typography style={{ display: 'block', marginTop: 16 }} variant="caption">
        Sample rate: {sampleRate}. Base Latency: {Math.round(mixer.context.baseLatency * 1000)} ms.
      </Typography>
    </VBox>
  )
}

export default AudioMixer
